import React, { useContext } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { intl } from "LocaleWrapper";

import { CopyToClipboard } from "@cofano/cck";

import {
	Contact,
	Contract,
	FeetStaffel,
	Fleet,
	GasolineStaffel,
	Location,
	User,
	WaterLevelStaffel,
} from "__generated__/graphql";

import { Call, Cargo, Charter, Product, Relation, Ship, Voyage } from "types/index";

import { ApplicationSettingsContext } from "components/hooks/useSettings";
import {
	hasPluginPermission,
	isAdmin,
	isCompanyUser,
	isCustomer,
	isFinance,
	isMainUserLimited,
	isMasterdata,
	isOperator,
	isShipManager,
	isSkipper,
	isTerminal,
} from "components/utils/Permission";
import { useStoreState } from "ezStore";
import { locationDescriptionWithGroup } from "view/locations/utils/LocationUtils";
import { CharterType } from "__generated__/enums";

const StyledBaseLink = styled.span`
	display: inline-flex;
	max-width: 100%;

	a {
		color: #1890ff;
		text-decoration: none;
	}

	.cck-copy-to-clipboard {
		display: flex;
		max-width: 100%;

		a {
			max-width: calc(100% - 18px);
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		> div {
			height: 14px !important;
			width: 20px !important;

			> div {
				align-items: center;
				display: flex;
				height: 14px !important;
				justify-content: center;

				svg {
					font-size: 16px !important;
				}
			}
		}
	}
`;

type BaseLinkProps = {
	text: string;
	url: string;
	state?: object;
};

const BaseLink = ({ text, url, state }: BaseLinkProps) => {
	return (
		<StyledBaseLink onClick={e => e.stopPropagation()}>
			<CopyToClipboard text={text}>
				<Link to={url} state={state}>{text}</Link>
			</CopyToClipboard>
		</StyledBaseLink>
	);
};

export const InvoiceLink = ({ invoice }: {
	invoice?: { id: number; invoiceNumber?: string | null } | null,
}) => {
	if (!invoice) {
		return <></>;
	}

	const label = invoice.invoiceNumber || `#${invoice.id}`;
	return <BaseLink text={label} url={`/view/invoices/${invoice.id}/details`} />;
};

export const ShipLink = ({ ship }: {
	ship?: Pick<Ship, "id" | "name"> | null,
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (ship && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={ship.name}
				url={`/view/ships/${ship.id}/details`} />
		);
	} else if (ship) {
		return ship.name;
	} else {
		return <React.Fragment />;
	}
};

export const ConnectingShipLink = ({ ship }: {
	ship?: Pick<Ship, "id" | "name"> | null,
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (ship && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={ship.name}
				url={"/view/connectingvisits/planning"} />
		);
	} else if (ship) {
		return ship.name;
	} else {
		return <React.Fragment />;
	}
};

export const LocationLink = ({ location, customName }: {
	location?: Pick<Location, "id" | "description"> & { locationGroups?: Pick<Location, "description" | "type">[] | null } | null
	customName?: string | null
}) => {

	const session = useStoreState(state => state.session);

	const showGroup = hasPluginPermission(session, "showLocationGroup");
	const description = showGroup && location ? locationDescriptionWithGroup(location) : location?.description;

	if (location && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={customName ?? description ?? ""}
				url={`/view/locations/${location.id}/details`} />
		);
	} else if (location) {
		return customName ?? description ?? "";
	} else {
		return <React.Fragment />;
	}
};

export const TerminalPlanningLink = ({ location } : {
	location?: Pick<Location, "id" | "description"> & { locationGroups?: Pick<Location, "description" | "type">[] | null } | null }) => {

	const session = useStoreState(state => state.session);

	const showGroup = hasPluginPermission(session, "showLocationGroup");
	const description = showGroup && location ? locationDescriptionWithGroup(location) : location?.description;

	if (location && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {

		const queryParams = new URLSearchParams({
			locationId: location.id.toString(),
			locationDescription: location.description,
		}).toString();

		return (
			<BaseLink
				text={description ?? ""}
				url={`/view/planning/terminals?${queryParams}`} />
		);
	} else if (location) {
		return description ?? "";
	} else {
		return <React.Fragment />;
	}
};

export const VoyageLink = ({ voyage }: {
	voyage?: Pick<Voyage, "id" | "voyageNumber" | "inactivityType"> & { ship: Pick<Ship, "id"> } | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (voyage && session && (
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session)
	)) {

		const skipperUrl = `/view/skipper/voyage/${voyage.id}?ship=${voyage.ship.id}`;
		return (
			<BaseLink
				text={voyage.voyageNumber}
				url={skipperUrl} />
		);
	} else if (voyage?.inactivityType) {
		return (
			<BaseLink
				text={`${voyage.voyageNumber} - ${intl.formatMessage({ id: `${voyage.inactivityType}_TIME` })}`}
				url={`/view/inactivity/${voyage.id}`} />
		);
	} else if (voyage && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={voyage.voyageNumber}
				url={`/view/voyages/${voyage.id}/details`} />
		);
	} else if (voyage) {
		return voyage.voyageNumber;
	} else {
		return <React.Fragment />;
	}
};

export const CargoLink = ({ cargo, customName }: {
	cargo?: {
		id: number,
		cargoNumber?: string | null | undefined,
		containerNumber?: string | null | undefined,
		product?: {
			id: number,
			name: string,
			description?: string | null | undefined
		} | null | undefined
	};
	customName?: string | null;
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	const name = customName || cargo?.cargoNumber || cargo?.containerNumber || cargo?.product?.name || cargo?.product?.description || "";

	if (cargo && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={name}
				url={`/view/cargos/${cargo.id}/details`} />
		);
	} else if (cargo) {
		return name;
	} else {
		return <React.Fragment />;
	}
};

export const CargoNominationNumberLink = ({ cargo }: {
	cargo?: Pick<Cargo, "id" | "nominationNumber"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (cargo?.nominationNumber && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={cargo.nominationNumber}
				url={`/view/cargos/${cargo.id}/details`} />
		);

	} else {
		return <React.Fragment />;
	}
};

export const FleetLink = ({ fleet }: {
	fleet?: Pick<Fleet, "id" | "description"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (fleet && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={fleet.description || ""}
				url={`/view/fleets/${fleet.id}/details`} />
		);
	} else if (fleet) {
		return fleet.description;
	} else {
		return <React.Fragment />;
	}
};

export const RelationLink = ({ relation }: {
	relation?: Pick<Relation, "id" | "name"> | null;
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (relation && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={relation.name}
				url={`/view/relations/${relation.id}/details`} />
		);
	} else if (relation) {
		return relation.name;
	} else {
		return <React.Fragment />;
	}
};

export const ContactLink = ({ relationId, contact }: {
	relationId?: number | null
	contact?: Pick<Contact, "id" | "name" | "email">  | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (relationId && contact && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={`${contact.name || ""} ${contact.email ? `(${contact.email})` : ""}`}
				url={`/view/relations/${relationId}/details?tab=contacts`} />
		);
	} else if (contact) {
		return contact.name;
	} else {
		return <React.Fragment />;
	}
};

export const ProductLink = ({ product, customName }: {
	product?: Pick<Product, "id" | "name"> | null
	customName?: string | null,
}) => {
	const { session } = useContext(ApplicationSettingsContext);

	if (product && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={customName || product.name}
				url={`/view/products/${product.id}/details`} />
		);
	} else if (product) {
		return customName || product.name;
	} else {
		return <React.Fragment />;
	}
};

export const GasolineStaffelLink = ({ gasolineStaffel }: {
	gasolineStaffel?: Pick<GasolineStaffel, "id" | "description"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (gasolineStaffel && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={gasolineStaffel.description || ""}
				url={`/view/staffels/${gasolineStaffel.id}/details/gasoline`} />
		);
	} else if (gasolineStaffel) {
		return gasolineStaffel.description;
	} else {
		return <React.Fragment />;
	}
};

export const WaterLevelStaffelLink = ({ waterLevelStaffel }: {
	waterLevelStaffel?: Pick<WaterLevelStaffel, "id" | "description"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (waterLevelStaffel && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={waterLevelStaffel.description || ""}
				url={`/view/staffels/${waterLevelStaffel.id}/details/waterlevel`} />
		);
	} else if (waterLevelStaffel) {
		return waterLevelStaffel.description;
	} else {
		return <React.Fragment />;
	}
};

export const FeetStaffelLink = ({ feetStaffel }: {
	feetStaffel?: Pick<FeetStaffel, "id" | "description"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (feetStaffel && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={feetStaffel.description || ""}
				url={`/view/staffels/${feetStaffel.id}/details/feet`} />
		);
	} else if (feetStaffel) {
		return feetStaffel.description;
	} else {
		return <React.Fragment />;
	}
};

export type CallLinkCallType =
	Pick<Call, "id"> & {
	location?: Pick<Location, | "description"> | null;
	voyage?: Pick<Voyage, "id"> | null;
}

export const CallLink = ({ call, description }: {
	call?: CallLinkCallType | null
	description?: string | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (call && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={description || call.location?.description || ""}
				url={`/view/voyages/${call.voyage?.id}/details?tab=details`}
				state={{
					selectedCallId: call.id,
				}} />
		);
	} else if (call) {
		return description || call.location?.description;
	} else {
		return <React.Fragment />;
	}
};

export const DossierLink = ({ charter }: {
	charter?: Pick<Charter, "id" | "charterNumber" | "type"> | null,
}) => {
	const session = useStoreState(state => state.session);

	if (!charter) {
		return <></>;
	}

	const hasAuthority = (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	);

	const showSalesCharterPage = hasAuthority && charter.type === CharterType.Sales;

	if (!hasAuthority) {
		return charter.charterNumber;
	}

	const url = showSalesCharterPage ? `/view/salescharters/${charter.id}/details` : `/view/purchasecharters/${charter.id}/details`;

	return (
		<BaseLink text={charter.charterNumber} url={url} />
	);
};

export const ContractLink = ({ contract }: {
	contract?: Pick<Contract, "id" | "contractNumber"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (contract && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={contract.contractNumber || ""}
				url={`/view/contracts/${contract.id}/details`} />
		);
	} else if (contract) {
		return contract.contractNumber;
	} else {
		return <React.Fragment />;
	}
};

export const UserLink = ({ user }: {
	user?: Pick<User, "id" | "username"> | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (user && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={user.username}
				url={`/view/users/${user.id}/details`} />
		);
	} else if (user) {
		return user.username;
	} else {
		return <React.Fragment />;
	}
};


export const WaterLevelLink = ({ name }: {
	name?: string | null
}) => {

	const { session } = useContext(ApplicationSettingsContext);

	if (name && session && (
		isAdmin(session) ||
		isCompanyUser(session) ||
		isCustomer(session) ||
		isShipManager(session) ||
		isSkipper(session) ||
		isOperator(session) ||
		isTerminal(session) ||
		isMasterdata(session) ||
		isFinance(session) ||
		isMainUserLimited(session)
	)) {
		return (
			<BaseLink
				text={name}
				url={"/view/waterlevels"} />
		);
	} else {
		return <React.Fragment />;
	}
};
